import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import Tabs from "../components/Tabs";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import {
  Mic, Square, Send, Loader, ArrowRight, Sparkles, Trophy, BookOpen, BookOpenCheck,
  RotateCcw, Download, Heart, MicOff, AlertCircle, ChevronDown, ChevronUp, Search,
} from "lucide-react";
import { ScoreRow, ScoreDetail, ratingFor } from "../components/reports/ScoreAnalysis";

/* ============================================================== */
/*  Helpers                                                        */
/* ============================================================== */
const RUBRIC_LABELS = {
  clarity: "Clarity",
  structure: "Structure",
  relevance: "Relevance",
  confidence: "Confidence",
};

function RubricBars({ rubric }) {
  if (!rubric) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="turn-rubric">
      {Object.entries(RUBRIC_LABELS).map(([key, label]) => {
        const score = rubric[key] ?? 0;
        const pct = (score / 5) * 100;
        return (
          <div key={key} data-testid={`rubric-${key}`}>
            <div className="flex justify-between items-baseline mb-1">
              <span className="font-mono-rm uppercase tracking-widest text-[10px]"
                    style={{ color: "#475569" }}>
                {label}
              </span>
              <span className="font-display text-sm" style={{ color: "#0F1B2D" }}>
                {score}<span style={{ color: "#94A3B8", fontSize: 11 }}>/5</span>
              </span>
            </div>
            <div
              className="h-2 w-full overflow-hidden"
              style={{ background: "#E2E8F0", border: "2px solid #0F1B2D", borderRadius: 2 }}
            >
              <div
                style={{
                  width: `${pct}%`, height: "100%",
                  background: score >= 4 ? "#20C1A7" : score >= 3 ? "#FFC145" : "#FF6A00",
                  transition: "width 400ms ease",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function downloadTranscriptPDF(session) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 48;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const wrap = (text, size = 11, bold = false, color = [15, 27, 45]) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text || "", maxWidth);
    lines.forEach((line) => {
      if (y > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += size + 4;
    });
  };

  wrap("Resmo · Mock Interview Transcript", 18, true);
  y += 4;
  wrap(`${session.role || "—"} · ${session.level || ""}`, 11, true, [71, 85, 105]);
  if (session.company) wrap(`Company target: ${session.company}`, 10, false, [71, 85, 105]);
  wrap(`Mode: ${session.mode === "promo" ? "ProMo (Senior+)" : "Resmo (Mid)"}`, 10, false, [71, 85, 105]);
  wrap(`Completed: ${(session.completed_at || "").slice(0, 19).replace("T", " ")}`, 10, false, [71, 85, 105]);
  y += 8;

  if (session.scorecard) {
    wrap(`Overall: ${session.scorecard.overall_score}/100`, 14, true, [255, 106, 0]);
    if (session.scorecard.headline) wrap(session.scorecard.headline, 11);
    y += 6;
    wrap("Strengths", 12, true);
    (session.scorecard.strengths || []).forEach((s) => wrap("• " + s, 11));
    y += 4;
    wrap("Fixes", 12, true);
    (session.scorecard.fixes || []).forEach((s) => wrap("• " + s, 11));
    y += 4;
    if (session.scorecard.next_step) {
      wrap("Next step: " + session.scorecard.next_step, 11, true);
    }
    y += 10;
  }

  wrap("Question-by-question", 14, true);
  y += 4;
  (session.turns || []).forEach((t, i) => {
    wrap(`Q${i + 1}. [${t.question?.category || "General"}] ${t.question?.q || ""}`, 12, true);
    wrap(`Your answer: ${t.answer_text || ""}`, 11);
    const r = t.rubric || {};
    wrap(
      `Rubric — Clarity ${r.clarity}/5 · Structure ${r.structure}/5 · Relevance ${r.relevance}/5 · Confidence ${r.confidence}/5`,
      10, false, [71, 85, 105],
    );
    if (t.coach_note) wrap("Coach: " + t.coach_note, 11);
    if (t.stronger_answer) wrap("Stronger answer: " + t.stronger_answer, 11);
    if (t.follow_up_probe) wrap("Follow-up probe: " + t.follow_up_probe, 11);
    y += 8;
  });

  doc.save(`mock-interview-${(session.id || "session").slice(0, 8)}.pdf`);
}

/* ============================================================== */
/*  Lobby — pick source + start                                    */
/* ============================================================== */
function Lobby() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useTheme();
  const isPro = mode === "promo";

  // Parse ?bank_id= deep-link from Interview Prep page
  const preselectedBankId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("bank_id") || "").trim();
  }, [location.search]);

  const [source, setSource] = useState(preselectedBankId ? "bank" : "adhoc");
  const [role, setRole] = useState("");
  const [level, setLevel] = useState(isPro ? "Director" : "Senior");
  const [jdText, setJdText] = useState("");
  const [company, setCompany] = useState("");
  const [numQ, setNumQ] = useState(5);
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState(preselectedBankId || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [bRes, sRes] = await Promise.all([
          api.get("/interview-questions"),
          api.get("/mock-interview"),
        ]);
        if (!alive) return;
        setBanks(bRes.data || []);
        setRecentSessions((sRes.data || []).slice(0, 6));
      } catch {/* non-fatal */}
    })();
    return () => { alive = false; };
  }, []);

  const start = async () => {
    setError("");
    if (source === "adhoc" && !role.trim()) {
      setError("Add a role title (e.g. 'Senior Product Manager').");
      return;
    }
    if (source === "bank" && !bankId) {
      setError("Pick a saved question bank.");
      return;
    }
    setLoading(true);
    try {
      const body = source === "bank"
        ? { source: "bank", bank_id: bankId, num_questions: numQ }
        : { source: "adhoc", role: role.trim(), level: level.trim(),
            jd_text: jdText.trim(), company: company.trim(), num_questions: numQ };
      const res = await api.post("/mock-interview/start", body);
      navigate(`/mock-interview/${res.data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't start the session.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-8 pt-8 pb-12">
        <div className="rm-tag mb-3" data-testid="mock-lobby-tag">
          <Sparkles size={12} /> Practice room
        </div>
        <h1
          className="font-display uppercase leading-tight mb-2"
          style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", letterSpacing: "-0.025em" }}
          data-testid="mock-lobby-title"
        >
          {isPro ? <>Run a <span className="rm-text-orange">mock interview</span>.</>
                 : <>Let's <span className="rm-text-orange">practice</span> together.</>}
        </h1>
        <p className="text-sm md:text-base mb-8 max-w-2xl" style={{ color: "#475569" }}>
          {isPro
            ? "Pick a role + Job Description. We'll run 3–7 questions, score each answer on a 4-point rubric, give you a stronger version, and end with a scorecard."
            : "Pick a role, paste the Job Description if you have it, and Resmo will ask you 3–7 questions one at a time — with coaching feedback after each answer."}
        </p>

        {/* Deep-link confirmation when arriving from an Interview Prep bank */}
        {preselectedBankId && (
          <div
            className="rm-card-static p-4 mb-6 flex items-start gap-3"
            style={{ background: "#FFFBEB", borderColor: "#FFC145", boxShadow: "4px 4px 0 0 #FF6A00" }}
            data-testid="lobby-prefilled-bank-banner"
          >
            <Sparkles size={18} className="rm-text-orange mt-0.5 flex-shrink-0" />
            <div>
              <div className="rm-label mb-1">Pulling from your Interview Prep bank</div>
              <p className="text-sm" style={{ color: "#475569" }}>
                We've pre-selected the bank for you. Pick how many questions you want and hit start.
              </p>
            </div>
          </div>
        )}

        {/* Playbook CTA — one-page cheat sheet to prep BEFORE the live mock */}
        <div
          className="rm-card-static p-5 mb-6 flex items-start gap-4 flex-wrap"
          style={{ background: "#0F1B2D", color: "white", borderColor: "#FFC145", boxShadow: "5px 5px 0 0 #FF6A00" }}
          data-testid="lobby-playbook-cta"
        >
          <BookOpenCheck size={22} style={{ color: "#FFC145" }} className="flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-[220px]">
            <div className="rm-label mb-1" style={{ color: "#FFC145" }}>
              New · Interview Playbook
            </div>
            <div className="font-display text-xl uppercase leading-tight mb-1">
              Build your one-page cheat sheet first.
            </div>
            <p className="text-sm" style={{ color: "#CBD5E1" }}>
              Resmo drafts likely questions + pulls evidence bullets from your resume. Edit it,
              practice it, then come back here for the live mock. Includes STAR-framed answers
              for tough questions.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/playbooks")}
            className="rm-btn text-xs px-4 py-2 flex-shrink-0"
            style={{ background: "#FFC145", color: "#0F1B2D", border: "3px solid #FFC145" }}
            data-testid="lobby-open-playbooks-btn"
          >
            Open Playbook
          </button>
        </div>

        {/* Interviewer Research CTA — Perplexity-powered briefing on a specific person */}
        <div
          className="rm-card-static p-5 mb-6 flex items-start gap-4 flex-wrap"
          style={{ background: "#ECFDF5", color: "#064E3B", borderColor: "#047857", boxShadow: "5px 5px 0 0 #047857" }}
          data-testid="lobby-interviewer-cta"
        >
          <Search size={22} style={{ color: "#047857" }} className="flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                 style={{ color: "#047857", fontWeight: 700 }}>
              · New · Interviewer Research · Perplexity-powered
            </div>
            <div className="font-display text-xl uppercase leading-tight mb-1">
              Know who's interviewing you.
            </div>
            <p className="text-sm" style={{ color: "#0F1B2D" }}>
              Paste their LinkedIn URL (or name + company) — Resmo web-searches
              public sources and returns a briefing, talking points, likely
              questions, and dos &amp; don'ts in under 15 seconds.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/interviewer-research")}
            className="rm-btn text-xs px-4 py-2 flex-shrink-0"
            style={{ background: "#047857", color: "white", border: "3px solid #064E3B" }}
            data-testid="lobby-open-interviewer-btn"
          >
            Research them →
          </button>
        </div>

        {/* Source picker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setSource("adhoc")}
            data-testid="lobby-source-adhoc"
            className="rm-card-static p-4 text-left"
            style={{
              background: source === "adhoc" ? "#0F1B2D" : "white",
              color: source === "adhoc" ? "white" : "#0F1B2D",
              borderColor: source === "adhoc" ? "#FF6A00" : "#0F1B2D",
              boxShadow: source === "adhoc" ? "5px 5px 0 0 #FF6A00" : undefined,
              cursor: "pointer",
            }}
          >
            <div className="rm-label mb-1" style={{ color: source === "adhoc" ? "#FFC145" : "#475569" }}>
              Option A
            </div>
            <div className="font-display text-xl uppercase leading-none mb-2">Fresh role + Job Description</div>
            <p className="text-sm" style={{ color: source === "adhoc" ? "#CBD5E1" : "#475569" }}>
              Type a role and (optionally) paste a Job Description. We'll generate fresh questions.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setSource("bank")}
            data-testid="lobby-source-bank"
            className="rm-card-static p-4 text-left"
            style={{
              background: source === "bank" ? "#0F1B2D" : "white",
              color: source === "bank" ? "white" : "#0F1B2D",
              borderColor: source === "bank" ? "#FF6A00" : "#0F1B2D",
              boxShadow: source === "bank" ? "5px 5px 0 0 #FF6A00" : undefined,
              cursor: "pointer",
            }}
          >
            <div className="rm-label mb-1" style={{ color: source === "bank" ? "#FFC145" : "#475569" }}>
              Option B
            </div>
            <div className="font-display text-xl uppercase leading-none mb-2">Saved question bank</div>
            <p className="text-sm" style={{ color: source === "bank" ? "#CBD5E1" : "#475569" }}>
              Pull from a question bank you generated under Interview Prep.
            </p>
          </button>
        </div>

        <div className="rm-card-static p-5 md:p-6 mb-6" style={{ background: "white" }}>
          {source === "adhoc" ? (
            <div className="space-y-4">
              <div>
                <label className="rm-label">Role title</label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g. Senior Product Manager"
                  className="rm-input mt-1"
                  data-testid="lobby-input-role"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="rm-label">Experience level</label>
                  <input
                    type="text"
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    placeholder="Senior / Staff / Director"
                    className="rm-input mt-1"
                    data-testid="lobby-input-level"
                  />
                </div>
                <div>
                  <label className="rm-label">Target company (optional)</label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="e.g. Stripe"
                    className="rm-input mt-1"
                    data-testid="lobby-input-company"
                  />
                </div>
              </div>
              <div>
                <label className="rm-label">Job description (optional but recommended)</label>
                <textarea
                  rows={6}
                  value={jdText}
                  onChange={(e) => setJdText(e.target.value)}
                  placeholder="Paste the Job Description here. The fuller the description, the sharper the questions."
                  className="rm-input mt-1 font-mono-rm text-xs"
                  data-testid="lobby-input-jd"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="rm-label">Pick a saved question bank</label>
              {banks.length === 0 ? (
                <p className="text-sm mt-2" style={{ color: "#475569" }}>
                  You don't have any saved banks yet.{" "}
                  <a href="/resources/interview/new" className="rm-text-orange underline">
                    Build one in Interview Prep
                  </a>{" "}
                  first, then come back.
                </p>
              ) : (
                <select
                  className="rm-input mt-1"
                  value={bankId}
                  onChange={(e) => setBankId(e.target.value)}
                  data-testid="lobby-select-bank"
                >
                  <option value="">— Choose a bank —</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.role} · {b.level} ({(b.created_at || "").slice(0, 10)})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Question count */}
          <div className="mt-5">
            <label className="rm-label">How many questions?</label>
            <div className="flex gap-2 mt-2 flex-wrap" data-testid="lobby-num-questions">
              {[3, 5, 7].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNumQ(n)}
                  className="rm-btn"
                  style={{
                    background: numQ === n ? "#FF6A00" : "white",
                    color: numQ === n ? "white" : "#0F1B2D",
                    borderColor: "#0F1B2D",
                    boxShadow: numQ === n ? "3px 3px 0 0 #0F1B2D" : "3px 3px 0 0 rgba(15,27,45,0.15)",
                  }}
                  data-testid={`lobby-num-${n}`}
                >
                  {n} {n === 3 ? "(quick · ~10 min)" : n === 5 ? "(standard · ~20 min)" : "(deep · ~30 min)"}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 font-mono-rm text-xs"
                 style={{ background: "#FEF2F2", border: "2px solid #EF4444", color: "#991B1B" }}
                 data-testid="lobby-error">
              <AlertCircle size={12} className="inline mr-1" /> {error}
            </div>
          )}

          <div className="mt-5">
            <button
              type="button"
              onClick={start}
              disabled={loading}
              className="rm-btn rm-btn-primary w-full md:w-auto justify-center"
              data-testid="lobby-start-btn"
            >
              {loading ? <><Loader size={16} className="animate-spin" /> Building your loop…</>
                       : <><ArrowRight size={16} /> Start the session</>}
            </button>
            {loading && (
              <div className="mt-3 max-w-sm">
                <ProgressMeter expectedSeconds={30} label="Building your interview loop" testid="mock-start-progress-meter" />
              </div>
            )}
          </div>
        </div>

        {/* Recent sessions */}
        {recentSessions.length > 0 && (
          <div className="mt-2">
            <div className="rm-label mb-2">Recent practice runs</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lobby-recent-sessions">
              {recentSessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => navigate(`/mock-interview/${s.id}`)}
                  className="rm-card-static p-4 text-left transition-transform hover:-translate-y-0.5"
                  style={{ background: "white", cursor: "pointer" }}
                  data-testid={`recent-session-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display text-base uppercase">{s.role || "Untitled role"}</span>
                    <span
                      className="font-mono-rm text-[10px] uppercase px-1.5 py-0.5"
                      style={{
                        background: s.status === "complete" ? "#20C1A7" : "#FFC145",
                        color: "#0F1B2D",
                        borderRadius: 2,
                      }}
                    >
                      {s.status === "complete" ? "Complete" : "In progress"}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: "#475569" }}>
                    {s.level || "—"} · {(s.questions || []).length} questions · {(s.created_at || "").slice(0, 10)}
                    {s.scorecard?.overall_score != null && (
                      <> · <strong className="rm-text-orange">{s.scorecard.overall_score}/100</strong></>
                    )}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
/*  Runner — in-progress session                                   */
/* ============================================================== */
function Runner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isPro = mode === "promo";

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [answerText, setAnswerText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [latestTurn, setLatestTurn] = useState(null);
  const [showStronger, setShowStronger] = useState(false);

  const [finishing, setFinishing] = useState(false);
  const [savingStone, setSavingStone] = useState(false);
  const [stoneSavedId, setStoneSavedId] = useState(null);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recError, setRecError] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/mock-interview/${id}`);
        if (!alive) return;
        setSession(res.data);
        if (res.data?.saved_to_stone_id) setStoneSavedId(res.data.saved_to_stone_id);
      } catch (e) {
        if (!alive) return;
        setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load session.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const currentIndex = session?.current_index ?? 0;
  const totalQ = session?.questions?.length ?? 0;
  const currentQuestion = useMemo(
    () => (session?.questions || [])[currentIndex] || null,
    [session, currentIndex],
  );
  const progress = totalQ > 0 ? Math.round((currentIndex / totalQ) * 100) : 0;

  const submitAnswer = async () => {
    if (!answerText.trim()) {
      setError("Type or record an answer first.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post(`/mock-interview/${id}/answer`, {
        answer_text: answerText.trim(),
      });
      setLatestTurn(res.data.turn);
      setSession((prev) => prev ? {
        ...prev,
        turns: [...(prev.turns || []), res.data.turn],
        current_index: res.data.next_index ?? totalQ,
        status: res.data.done ? "awaiting_finalize" : "in_progress",
      } : prev);
      setAnswerText("");
      setShowStronger(false);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Scoring failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const advance = () => {
    setLatestTurn(null);
    setShowStronger(false);
  };

  const finalize = async () => {
    setFinishing(true);
    setError("");
    try {
      const res = await api.post(`/mock-interview/${id}/finish`, {});
      setSession(res.data);
      if (res.data?.status === "complete") {
        window.dispatchEvent(new CustomEvent("resmo:spark", { detail: { kind: "interview" } }));
      }
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't finalize.");
    } finally {
      setFinishing(false);
    }
  };

  const saveToStones = async () => {
    setSavingStone(true);
    setError("");
    try {
      const res = await api.post(`/mock-interview/${id}/save-to-stones`, {});
      setStoneSavedId(res.data.id);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save to Stepping Stones.");
    } finally {
      setSavingStone(false);
    }
  };

  // Audio recording -> transcribe
  const startRecording = async () => {
    setRecError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setRecError("Your browser doesn't support audio recording. Type your answer instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size === 0) {
          setRecError("Recording was empty. Try again.");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "answer.webm");
          const res = await api.post(
            `/mock-interview/${id}/transcribe-audio`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          const text = (res.data?.text || "").trim();
          if (text) {
            setAnswerText((prev) => prev ? (prev + " " + text).trim() : text);
          } else {
            setRecError("Couldn't hear anything in that recording.");
          }
        } catch (e) {
          setRecError(formatApiErrorDetail(e?.response?.data?.detail) || "Transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setRecError("Mic permission denied. Type your answer instead.");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-12 pb-12" data-testid="mock-runner-loading">
          <Loader size={28} className="animate-spin rm-text-orange" />
          <div className="rm-label mt-3">Loading session…</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-12 pb-12">
          <div className="rm-card-static p-5" style={{ background: "white" }} data-testid="mock-runner-error">
            <div className="rm-label mb-2">Session not found</div>
            <p className="text-sm" style={{ color: "#475569" }}>{error || "Couldn't load this session."}</p>
            <button onClick={() => navigate("/mock-interview")}
                    className="rm-btn rm-btn-primary mt-3" data-testid="back-to-lobby">
              ← Back to lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isComplete = session.status === "complete" && session.scorecard;
  const allAnswered = (session.turns || []).length >= totalQ && totalQ > 0;

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-8 pb-12">

        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="rm-tag mb-2" data-testid="runner-tag">
              <Sparkles size={12} /> Mock interview · {isPro ? "ProMo" : "Resmo"} tone
            </div>
            <h1 className="font-display text-2xl md:text-3xl uppercase leading-none">
              {session.role || "Mock interview"}
            </h1>
            <p className="text-xs mt-1 font-mono-rm uppercase tracking-widest" style={{ color: "#475569" }}>
              {session.level} · {totalQ} questions {session.company && <> · {session.company}</>}
            </p>
          </div>
          <button onClick={() => navigate("/mock-interview")}
                  className="rm-btn rm-btn-ghost text-xs" data-testid="runner-leave-btn">
            Leave session
          </button>
        </div>

        {/* Progress */}
        {!isComplete && (
          <div className="mb-6" data-testid="runner-progress">
            <div className="flex justify-between items-baseline mb-1">
              <span className="rm-label">
                {allAnswered ? "All answered" : `Question ${Math.min(currentIndex + 1, totalQ)} of ${totalQ}`}
              </span>
              <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#475569" }}>
                {progress}%
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden"
                 style={{ background: "#E2E8F0", border: "2px solid #0F1B2D", borderRadius: 2 }}>
              <div style={{ width: `${progress}%`, height: "100%", background: "#FF6A00",
                            transition: "width 400ms ease" }} />
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 font-mono-rm text-xs"
               style={{ background: "#FEF2F2", border: "2px solid #EF4444", color: "#991B1B" }}
               data-testid="runner-error">
            <AlertCircle size={12} className="inline mr-1" /> {error}
          </div>
        )}

        {/* === Mode A: Show feedback for the just-submitted turn === */}
        {latestTurn && !isComplete && (
          <div className="rm-card-static p-5 mb-6" style={{ background: "white" }} data-testid="turn-feedback">
            <div className="rm-label mb-2" style={{ color: "#FF6A00" }}>Coach feedback</div>
            <RubricBars rubric={latestTurn.rubric} />
            <div className="mt-4 p-3" style={{ background: "#FFFBEB", border: "2px solid #FFC145" }}
                 data-testid="coach-note-box">
              <div className="rm-label mb-1">Coach note</div>
              <p className="text-sm leading-snug">{latestTurn.coach_note}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowStronger((s) => !s)}
              className="mt-3 flex items-center gap-1 font-mono-rm text-xs uppercase tracking-widest rm-text-orange"
              data-testid="toggle-stronger-answer"
            >
              {showStronger ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showStronger ? "Hide stronger answer" : "Show stronger answer"}
            </button>
            {showStronger && (
              <div className="mt-2 p-3" style={{ background: "#F1ECDF", border: "2px solid #0F1B2D" }}
                   data-testid="stronger-answer-box">
                <p className="text-sm leading-snug italic">"{latestTurn.stronger_answer}"</p>
              </div>
            )}
            {latestTurn.follow_up_probe && (
              <div className="mt-3 p-3" style={{ background: "#0F1B2D", color: "white" }}
                   data-testid="follow-up-probe">
                <div className="rm-label mb-1" style={{ color: "#FFC145" }}>Follow-up an interviewer would ask</div>
                <p className="text-sm leading-snug">{latestTurn.follow_up_probe}</p>
              </div>
            )}
            <div className="mt-5 flex gap-2 flex-wrap">
              {!allAnswered ? (
                <button onClick={advance} className="rm-btn rm-btn-primary" data-testid="next-question-btn">
                  <ArrowRight size={14} /> Next question
                </button>
              ) : (
                <button onClick={finalize} disabled={finishing}
                        className="rm-btn rm-btn-primary" data-testid="finalize-btn">
                  {finishing ? <><Loader size={14} className="animate-spin" /> Scoring the loop…</>
                             : <><Trophy size={14} /> See my scorecard</>}
                </button>
              )}
            </div>
            {finishing && (
              <div className="mt-3 max-w-sm">
                <ProgressMeter expectedSeconds={20} label="Scoring the full loop" testid="mock-finalize-inline-progress-meter" />
              </div>
            )}
          </div>
        )}

        {/* === Mode B: Question + answer input === */}
        {!latestTurn && !isComplete && currentQuestion && (
          <div className="rm-card-static p-5 mb-6" style={{ background: "white" }} data-testid="question-card">
            <span className="font-mono-rm uppercase tracking-widest text-[10px] px-2 py-1 inline-block mb-3"
                  style={{ background: "#0F1B2D", color: "#FFC145", borderRadius: 2 }}
                  data-testid="question-category">
              {currentQuestion.category}
            </span>
            <h2 className="font-display text-xl md:text-2xl uppercase leading-tight mb-2"
                data-testid="question-text">
              {currentQuestion.q}
            </h2>
            {currentQuestion.why && (
              <p className="text-xs italic mb-4" style={{ color: "#475569" }} data-testid="question-why">
                Why they ask: {currentQuestion.why}
              </p>
            )}

            <textarea
              rows={7}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Type your answer here. STAR works well for behavioral questions."
              className="rm-input font-mono-rm text-xs"
              disabled={submitting || transcribing}
              data-testid="answer-textarea"
            />
            <div className="flex justify-between items-center mt-1">
              <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
                {answerText.length} chars
              </span>
            </div>

            {recError && (
              <div className="mt-2 font-mono-rm text-xs" style={{ color: "#991B1B" }} data-testid="rec-error">
                <MicOff size={12} className="inline mr-1" /> {recError}
              </div>
            )}

            <div className="mt-4 flex gap-2 flex-wrap">
              {!recording ? (
                <button onClick={startRecording} disabled={submitting || transcribing}
                        className="rm-btn rm-btn-ghost" data-testid="record-start-btn">
                  {transcribing ? <><Loader size={14} className="animate-spin" /> Transcribing…</>
                                : <><Mic size={14} /> Record answer</>}
                </button>
              ) : (
                <button onClick={stopRecording}
                        className="rm-btn"
                        style={{ background: "#EF4444", color: "white", border: "3px solid #0F1B2D",
                                 boxShadow: "3px 3px 0 0 #0F1B2D" }}
                        data-testid="record-stop-btn">
                  <Square size={14} /> Stop recording
                </button>
              )}
              <button onClick={submitAnswer} disabled={submitting || transcribing || !answerText.trim()}
                      className="rm-btn rm-btn-primary" data-testid="submit-answer-btn">
                {submitting ? <><Loader size={14} className="animate-spin" /> Scoring…</>
                            : <><Send size={14} /> Submit answer</>}
              </button>
            </div>
            {(submitting || transcribing) && (
              <div className="mt-3 max-w-sm">
                <ProgressMeter expectedSeconds={submitting ? 15 : 10}
                               label={submitting ? "Scoring your answer" : "Transcribing your recording"}
                               testid="mock-answer-progress-meter" />
              </div>
            )}
          </div>
        )}

        {/* === Mode C: All answered, not yet finalized === */}
        {!latestTurn && !isComplete && allAnswered && (
          <div className="rm-card-static p-5 mb-6" style={{ background: "#FFFBEB" }} data-testid="ready-to-finalize">
            <div className="rm-label mb-2">All answered</div>
            <p className="text-sm mb-3" style={{ color: "#475569" }}>
              You've answered all {totalQ} questions. Ready to see your overall scorecard?
            </p>
            <button onClick={finalize} disabled={finishing}
                    className="rm-btn rm-btn-primary" data-testid="finalize-btn-ready">
              {finishing ? <><Loader size={14} className="animate-spin" /> Scoring the loop…</>
                         : <><Trophy size={14} /> See my scorecard</>}
            </button>
            {finishing && (
              <div className="mt-3 max-w-sm">
                <ProgressMeter expectedSeconds={20} label="Scoring the full loop" testid="mock-finalize-progress-meter" />
              </div>
            )}
          </div>
        )}

        {/* === Mode D: Final scorecard === */}
        {isComplete && (
          <FinalScorecard
            session={session}
            onSaveToStones={saveToStones}
            savingStone={savingStone}
            stoneSavedId={stoneSavedId}
            onDownloadPdf={() => downloadTranscriptPDF(session)}
            onRunAnother={() => navigate("/mock-interview")}
          />
        )}

      </div>
    </div>
  );
}

/* ============================================================== */
/*  Final Scorecard                                                */
/* ============================================================== */
function FinalScorecard({ session, onSaveToStones, savingStone, stoneSavedId, onDownloadPdf, onRunAnother }) {
  const s = session.scorecard || {};
  const score = s.overall_score ?? 0;
  const scoreColor = score >= 75 ? "#20C1A7" : score >= 55 ? "#FFC145" : "#FF6A00";
  const turnCount = (session.turns || []).length;
  const [turnSel, setTurnSel] = useState(null);

  const turnScore = (t) => {
    const vals = Object.values(t?.rubric || {}).map(Number).filter((v) => !isNaN(v));
    if (!vals.length) return 0;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 20);
  };

  return (
    <div data-testid="final-scorecard">
      <div className="rm-card-static p-6 mb-5"
           style={{ background: "#0F1B2D", color: "white", borderColor: "#FFC145",
                    boxShadow: "6px 6px 0 0 #FF6A00" }}
           data-testid="scorecard-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="rm-label mb-1" style={{ color: "#FFC145" }}>Overall</div>
            <div className="font-display leading-none"
                 style={{ fontSize: "clamp(3rem, 8vw, 5rem)", color: scoreColor }}
                 data-testid="scorecard-overall-score">
              {score}<span style={{ fontSize: "0.4em", color: "#CBD5E1" }}>/100</span>
            </div>
            {s.headline && (
              <p className="mt-2 text-sm md:text-base leading-snug" data-testid="scorecard-headline">{s.headline}</p>
            )}
          </div>
          <Trophy size={64} style={{ color: scoreColor, opacity: 0.85 }} />
        </div>
      </div>

      <Tabs
        testid="scorecard-tabs"
        defaultId="summary"
        items={[
          { id: "summary",    label: "Summary" },
          { id: "transcript", label: "Per-Question Breakdown", count: turnCount },
        ]}
      >
      {(activeTab) => (<>

      {activeTab === "summary" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="rm-card-static p-5" style={{ background: "white" }} data-testid="scorecard-strengths">
              <div className="rm-label mb-2" style={{ color: "#20C1A7" }}>Strengths</div>
              <ul className="space-y-2 text-sm">
                {(s.strengths || []).map((str, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: "#20C1A7" }}>▸</span>
                    <span>{str}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rm-card-static p-5" style={{ background: "white" }} data-testid="scorecard-fixes">
              <div className="rm-label mb-2" style={{ color: "#FF6A00" }}>Fixes for next rep</div>
              <ul className="space-y-2 text-sm">
                {(s.fixes || []).map((str, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="rm-text-orange">▸</span>
                    <span>{str}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {s.next_step && (
            <div className="rm-card-static p-4 mb-5"
                 style={{ background: "#FFFBEB", borderColor: "#FFC145" }}
                 data-testid="scorecard-next-step">
              <div className="rm-label mb-1">Next step</div>
              <p className="text-sm">{s.next_step}</p>
            </div>
          )}

          {/* Actions */}
          <div className="rm-card-static p-5" style={{ background: "white" }} data-testid="scorecard-actions">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={onSaveToStones}
                disabled={savingStone || !!stoneSavedId}
                className="rm-btn rm-btn-primary justify-center"
                data-testid="save-to-stones-btn"
              >
                {stoneSavedId ? <><Heart size={14} /> Saved to log</>
                 : savingStone ? <><Loader size={14} className="animate-spin" /> Saving…</>
                 : <><Heart size={14} /> Save to Stepping Stones</>}
              </button>
              <button
                type="button"
                onClick={onDownloadPdf}
                className="rm-btn rm-btn-ghost justify-center"
                data-testid="download-pdf-btn"
              >
                <Download size={14} /> Download transcript PDF
              </button>
              <button
                type="button"
                onClick={onRunAnother}
                className="rm-btn rm-btn-ghost justify-center"
                data-testid="run-another-btn"
              >
                <RotateCcw size={14} /> Run another mock
              </button>
            </div>
            {stoneSavedId && (
              <p className="mt-3 font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#20C1A7" }}>
                ✓ Saved as a Stepping Stone — find it in your log.
              </p>
            )}
          </div>
        </>
      )}

      {activeTab === "transcript" && (
        <div className="space-y-2" data-testid="transcript-list">
          {(session.turns || []).map((t, i) => (
            <ScoreRow
              key={i}
              score={turnScore(t)}
              title={`Q${i + 1} · ${t.question?.q || ""}`}
              description={t.coach_note || t.question?.category}
              onClick={() => setTurnSel(i)}
              testId={`transcript-turn-${i}`}
            />
          ))}
          {turnSel != null && (session.turns || [])[turnSel] && (() => {
            const t = session.turns[turnSel];
            return (
              <ScoreDetail
                open
                onClose={() => setTurnSel(null)}
                score={turnScore(t)}
                eyebrow={`Question ${turnSel + 1} · ${t.question?.category || "answer breakdown"}`}
                title={t.question?.q || ""}
                testId="transcript-detail"
              >
                <RubricBars rubric={t.rubric} />
                <div className="mt-4 p-3" style={{ background: "#F8FAFC", border: "2px solid #E2E8F0" }}>
                  <div className="rm-label mb-1">Your answer</div>
                  <p className="text-sm whitespace-pre-wrap">{t.answer_text}</p>
                </div>
                {t.coach_note && (
                  <div className="mt-3 p-3" style={{ background: "#FFFBEB", border: "2px solid #FFC145" }}>
                    <div className="rm-label mb-1">Coach note</div>
                    <p className="text-sm">{t.coach_note}</p>
                  </div>
                )}
                {t.stronger_answer && (
                  <div className="mt-3 p-3" style={{ background: "#F1ECDF", border: "2px solid #0F1B2D" }}>
                    <div className="rm-label mb-1">Stronger answer</div>
                    <p className="text-sm italic">"{t.stronger_answer}"</p>
                  </div>
                )}
                {t.follow_up_probe && (
                  <div className="mt-3 p-3" style={{ background: "#0F1B2D", color: "white" }}>
                    <div className="rm-label mb-1" style={{ color: "#FFC145" }}>Follow-up probe</div>
                    <p className="text-sm">{t.follow_up_probe}</p>
                  </div>
                )}
              </ScoreDetail>
            );
          })()}
        </div>
      )}

      </>)}
      </Tabs>
    </div>
  );
}

/* ============================================================== */
/*  Default export — routes by URL                                 */
/* ============================================================== */
export default function MockInterview({ mode }) {
  const { id } = useParams();
  if (mode === "runner" || id) return <Runner />;
  return <Lobby />;
}

export { Lobby as MockInterviewLobby, Runner as MockInterviewRunner };
